"use client";

import { ChangeEvent, useEffect, useState } from "react";

const initialForm = {
  projectName: "",
  linkUrl: "",
  contactEmail: "",
  contactTelegram: "",
  contactTwitter: "",
  message: "",
  promoCode: "",
};

const ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/gif", "image/webp"];

export default function AdvertisePage() {
  const [form, setForm] = useState(initialForm);
  const [bannerImage, setBannerImage] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [duration, setDuration] = useState<{ days: number; price: number } | null>(null);
  const [agreedToRules, setAgreedToRules] = useState(false);
  const [promoCodeValid, setPromoCodeValid] = useState<boolean | null>(null);
  const [promoCodeMarketer, setPromoCodeMarketer] = useState<string | null>(null);
  const [checkingPromoCode, setCheckingPromoCode] = useState(false);

  useEffect(() => {
    if (!bannerImage) {
      setPreviewUrl("");
      return;
    }

    const objectUrl = URL.createObjectURL(bannerImage);
    setPreviewUrl(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [bannerImage]);

  const update = (key: keyof typeof initialForm, value: string) => {
    setForm((current) => ({ ...current, [key]: value }));
    // Reset promo code validation when code changes
    if (key === "promoCode") {
      setPromoCodeValid(null);
      setPromoCodeMarketer(null);
    }
  };

  const validatePromoCode = async () => {
    const code = form.promoCode.trim();
    if (!code) {
      setPromoCodeValid(null);
      setPromoCodeMarketer(null);
      return;
    }

    setCheckingPromoCode(true);
    try {
      const res = await fetch("/api/promo-codes/validate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code }),
      });
      const data = await res.json();
      setPromoCodeValid(data.valid);
      setPromoCodeMarketer(data.valid ? data.marketerName : null);
    } catch {
      setPromoCodeValid(false);
      setPromoCodeMarketer(null);
    } finally {
      setCheckingPromoCode(false);
    }
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;

    if (file && !ACCEPTED_TYPES.includes(file.type)) {
      setError("Please upload a JPG, PNG, GIF, or WEBP banner image.");
      setBannerImage(null);
      return;
    }

    if (!file) {
      setError("");
      setBannerImage(null);
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    const img = new Image();

    img.onload = () => {
      const validSizes = [
        { w: 1500, h: 500 },
        { w: 600, h: 200 },
      ];
      const isValidSize = validSizes.some(s => s.w === img.width && s.h === img.height);
      if (!isValidSize) {
        setError(`Banner must be 1500x500px (Twitter) or 600x200px (DEXScreener). Your image is ${img.width}x${img.height}px.`);
        setBannerImage(null);
        URL.revokeObjectURL(objectUrl);
        return;
      }

      setError("");
      setBannerImage(file);
      URL.revokeObjectURL(objectUrl);
    };

    img.onerror = () => {
      setError("Unable to read image dimensions. Please try another file.");
      setBannerImage(null);
      URL.revokeObjectURL(objectUrl);
    };

    img.src = objectUrl;
  };

  const handleSubmit = async () => {
    if (!form.projectName.trim() || !form.linkUrl.trim() || !bannerImage) {
      setError("Please fill in Project Name, Banner Image, and Destination Link.");
      return;
    }

    if (!form.contactEmail.trim()) {
      setError("Contact email is required so we can send you payment instructions.");
      return;
    }

    if (!agreedToRules) {
      setError("Please agree to the content rules before submitting.");
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      const payload = new FormData();
      payload.append("projectName", form.projectName.trim());
      payload.append("bannerImage", bannerImage);
      payload.append("linkUrl", form.linkUrl.trim());
      payload.append("contactEmail", form.contactEmail.trim());
      payload.append("contactTelegram", form.contactTelegram.trim());
      payload.append("contactTwitter", form.contactTwitter.trim());
      payload.append("message", form.message.trim());

      if (duration) {
        payload.append("adDuration", `${duration.days} days`);
        payload.append("adPrice", `$${duration.price}`);
      }

      if (form.promoCode.trim()) {
        payload.append("promoCode", form.promoCode.trim().toLowerCase());
      }

      const res = await fetch("/api/ads/submit", {
        method: "POST",
        body: payload,
      });
      const data = await res.json();

      if (!res.ok || !data.success) {
        setError(data.error || "Submission failed. Please try again.");
        return;
      }

      setSubmitted(true);
      setForm(initialForm);
      setBannerImage(null);
    } catch (e) {
      console.error(e);
      setError("Failed to submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div style={{ background: "#111", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Inter, sans-serif", padding: 24 }}>
        <div style={{ maxWidth: 560, textAlign: "center", padding: 40, background: "linear-gradient(180deg,rgba(22,22,26,0.95)_0%,rgba(16,16,20,0.95)_100%)", border: "1px solid rgba(255,255,255,0.07)", borderTop: "1px solid rgba(255,255,255,0.13)", borderRadius: 20, boxShadow: "0 8px 40px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.04)" }}>
          <div style={{ fontSize: 64, marginBottom: 24 }}>📣</div>
          <h1 style={{ color: "#f9cd4f", fontSize: 32, fontWeight: 700, marginBottom: 16 }}>Submission Received!</h1>
          <p style={{ color: "#aaa", fontSize: 16, lineHeight: 1.7, marginBottom: 8 }}>We'll review your ad and email you payment instructions within 12 hours.</p>
          <p style={{ color: "#aaa", fontSize: 16, lineHeight: 1.7 }}>Once payment is confirmed, your banner will go live.</p>
          <a href="/" style={{ display: "inline-block", marginTop: 32, background: "#f9cd4f", color: "#111", padding: "12px 32px", borderRadius: 10, fontWeight: 700, textDecoration: "none" }}>Back to ForgeSwap</a>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: "#111", minHeight: "100vh", color: "#fff", fontFamily: "Inter, sans-serif", overflow: "hidden", maxWidth: "100vw" }}>
      <div style={{ background: "linear-gradient(135deg, #1a1a1a, #111)", borderBottom: "1px solid #222", padding: "48px 16px 32px" }}>
        <div style={{ maxWidth: 840, margin: "0 auto", textAlign: "center" }}>
          <h1 style={{ color: "#f9cd4f", fontSize: "clamp(28px, 6vw, 42px)", fontWeight: 800, marginBottom: 16 }}>Advertise on ForgeSwap</h1>
          <p style={{ color: "#ccc", fontSize: 18, lineHeight: 1.7, maxWidth: 680, margin: "0 auto" }}>Reach thousands of active DeFi traders daily.</p>
        </div>
      </div>

      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "32px 16px 48px", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 32 }}>
        <div style={{ background: "linear-gradient(180deg,rgba(22,22,26,0.95)_0%,rgba(16,16,20,0.95)_100%)", border: "1px solid rgba(255,255,255,0.07)", borderTop: "1px solid rgba(255,255,255,0.13)", borderRadius: 20, padding: 28, boxShadow: "0 8px 40px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.04)" }}>
          <h2 style={{ color: "#f9cd4f", fontSize: 22, fontWeight: 700, margin: "0 0 8px" }}>Submit your campaign</h2>
          <p style={{ color: "#888", fontSize: 14, margin: "0 0 28px" }}>Share your banner, destination link, and contact info. We'll review and email you payment instructions.</p>

          {error && (
            <div style={{ background: "#ef444422", border: "1px solid #ef4444", color: "#ef4444", padding: "12px 16px", borderRadius: 8, marginBottom: 16, fontSize: 14 }}>
              {error}
            </div>
          )}

          <div style={{ display: "grid", gap: 16 }}>
            {[
              { key: "projectName", label: "Project Name *", placeholder: "e.g. Forge Alpha" },
              { key: "linkUrl", label: "Destination Link *", placeholder: "https://example.com" },
              { key: "contactEmail", label: "Contact Email *", placeholder: "team@example.com" },
              { key: "contactTelegram", label: "Contact Telegram", placeholder: "@projectteam" },
              { key: "contactTwitter", label: "Contact Twitter", placeholder: "@projectteam" },
            ].map((field) => (
              <div key={field.key}>
                <label style={{ display: "block", color: "#888", fontSize: 12, marginBottom: 6 }}>{field.label}</label>
                <input
                  value={form[field.key as keyof typeof initialForm]}
                  onChange={(e) => update(field.key as keyof typeof initialForm, e.target.value)}
                  placeholder={field.placeholder}
                  style={{ width: "100%", background: "#111", border: "1px solid #333", color: "#fff", padding: "12px 14px", borderRadius: 10, fontSize: 14, boxSizing: "border-box" }}
                />
              </div>
            ))}

            <div>
              <label style={{ display: "block", color: "#888", fontSize: 12, marginBottom: 6 }}>Banner Image *</label>
              <input
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                onChange={handleFileChange}
                style={{ width: "100%", background: "#111", border: "1px solid #333", color: "#fff", padding: "12px 14px", borderRadius: 10, fontSize: 14, boxSizing: "border-box" }}
              />
              <p style={{ color: "#666", fontSize: 12, margin: "8px 0 0" }}>{bannerImage ? `Selected: ${bannerImage.name}` : "Accepted sizes: 1500×500px (Twitter) or 600×200px (DEXScreener) · JPG, PNG, GIF, WEBP"}</p>
            </div>

            <div>
              <label style={{ display: "block", color: "#888", fontSize: 12, marginBottom: 10 }}>Preferred Duration</label>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8 }}>
                {[
                  { days: 1, price: 30 },
                  { days: 3, price: 75 },
                  { days: 5, price: 100 },
                  { days: 7, price: 130 },
                  { days: 14, price: 240 },
                  { days: 30, price: 400 },
                  { days: 60, price: 750 },
                  { days: 90, price: 1000 },
                ].map((tier) => (
                  <button
                    key={tier.days}
                    type="button"
                    onClick={() => setDuration(duration?.days === tier.days ? null : tier)}
                    style={{
                      background: duration?.days === tier.days ? "#f9cd4f" : "#111",
                      border: `1px solid ${duration?.days === tier.days ? "#f9cd4f" : "#333"}`,
                      color: duration?.days === tier.days ? "#111" : "#ccc",
                      padding: "10px 6px",
                      borderRadius: 10,
                      cursor: "pointer",
                      textAlign: "center",
                      fontSize: 12,
                      fontWeight: duration?.days === tier.days ? 700 : 400,
                    }}
                  >
                    <div style={{ fontWeight: 600 }}>{tier.days === 1 ? "1 day" : `${tier.days} days`}</div>
                    <div style={{ fontSize: 13, marginTop: 2 }}>${tier.price.toLocaleString()}</div>
                  </button>
                ))}
              </div>
              {duration && (
                <div style={{ marginTop: 10, color: "#888", fontSize: 12 }}>
                  Selected: {duration.days} {duration.days === 1 ? "day" : "days"} — ${duration.price.toLocaleString()} (${(duration.price / duration.days).toFixed(2)}/day)
                </div>
              )}
            </div>

            <div>
              <label style={{ display: "block", color: "#888", fontSize: 12, marginBottom: 6 }}>Promo Code</label>
              <input
                value={form.promoCode}
                onChange={(e) => update("promoCode", e.target.value)}
                onBlur={validatePromoCode}
                placeholder="Optional promo code"
                style={{
                  width: "100%",
                  background: "#111",
                  border: `1px solid ${promoCodeValid === true ? "#22c55e" : promoCodeValid === false ? "#ef4444" : "#333"}`,
                  color: "#fff",
                  padding: "12px 14px",
                  borderRadius: 10,
                  fontSize: 14,
                  boxSizing: "border-box",
                  textTransform: "uppercase",
                }}
              />
              {checkingPromoCode && (
                <div style={{ color: "#888", fontSize: 12, marginTop: 6 }}>Checking code...</div>
              )}
              {promoCodeValid === true && promoCodeMarketer && (
                <div style={{ color: "#22c55e", fontSize: 12, marginTop: 6 }}>✓ Code applied ({promoCodeMarketer})</div>
              )}
              {promoCodeValid === false && form.promoCode.trim() && (
                <div style={{ color: "#ef4444", fontSize: 12, marginTop: 6 }}>Invalid or inactive promo code</div>
              )}
            </div>

            <div style={{ background: "#1a1a1a", border: "1px solid #333", borderRadius: 10, padding: 16 }}>
              <p style={{ color: "#f9cd4f", fontSize: 14, fontWeight: 700, margin: "0 0 10px" }}>Content Rules</p>
              <ul style={{ color: "#aaa", fontSize: 13, lineHeight: 1.8, margin: "0 0 12px", paddingLeft: 16 }}>
                <li>No graphic, violent, or adult content</li>
                <li>No misleading or fraudulent projects</li>
                <li>No content targeting minors</li>
                <li>Banner must be 1500×500px or 600×200px</li>
                <li>All ads are reviewed within 12 hours</li>
                <li>Refunds for rejected ads are at team discretion</li>
              </ul>
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={agreedToRules}
                  onChange={(e) => setAgreedToRules(e.target.checked)}
                />
                <span style={{ color: "#ccc", fontSize: 13 }}>I have read and agree to the content rules</span>
              </label>
            </div>

            <div>
              <label style={{ display: "block", color: "#888", fontSize: 12, marginBottom: 6 }}>Message / Notes</label>
              <textarea
                value={form.message}
                onChange={(e) => update("message", e.target.value)}
                placeholder="Tell us about your campaign, timeline, and any placement preferences."
                rows={5}
                style={{ width: "100%", background: "#111", border: "1px solid #333", color: "#fff", padding: "12px 14px", borderRadius: 10, fontSize: 14, boxSizing: "border-box", resize: "vertical" }}
              />
            </div>

            <button
              onClick={handleSubmit}
              disabled={submitting}
              style={{ background: "#f9cd4f", color: "#111", border: "none", padding: "12px 18px", borderRadius: 10, fontWeight: 700, cursor: "pointer", fontSize: 15 }}
            >
              {submitting ? "Submitting..." : "Submit Banner Request"}
            </button>
          </div>
        </div>

        <div style={{ display: "grid", gap: 20 }}>
          <div style={{ background: "#1a1a1a", border: "1px solid #222", borderRadius: 16, padding: 24 }}>
            <h3 style={{ color: "#f9cd4f", fontSize: 18, fontWeight: 700, margin: "0 0 12px" }}>How it works</h3>
            <div style={{ display: "grid", gap: 12 }}>
              {[
                "1. Submit your banner and campaign details.",
                "2. We review your submission within 12 hours.",
                "3. If approved, we'll email you payment instructions.",
                "4. Once payment is confirmed, your banner goes live!",
              ].map((item) => (
                <div key={item} style={{ color: "#ccc", fontSize: 14, lineHeight: 1.7, background: "#111", border: "1px solid #222", borderRadius: 12, padding: 14 }}>
                  {item}
                </div>
              ))}
            </div>
          </div>

          <div style={{ background: "#1a1a1a", border: "1px solid #222", borderRadius: 16, padding: 24 }}>
            <h3 style={{ color: "#f9cd4f", fontSize: 18, fontWeight: 700, margin: "0 0 12px" }}>Banner Preview</h3>
            <div style={{ background: "#111", border: "1px solid #222", borderRadius: 14, padding: 16 }}>
              {previewUrl ? (
                <img src={previewUrl} alt="Banner preview" style={{ width: "100%", borderRadius: 12, display: "block", objectFit: "cover", maxHeight: 280 }} />
              ) : (
                <div style={{ minHeight: 180, display: "flex", alignItems: "center", justifyContent: "center", color: "#555", border: "1px dashed #333", borderRadius: 12 }}>
                  Banner preview will appear here
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
